import { useEffect, useId, useRef, type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { useT } from "../lib/i18n";

/** Alan hatası kodu → kullanıcı metni (sunucu `fields` haritası). */
export function useFieldError() {
  const t = useT();
  return (code?: string) => (code ? t(`fieldErrors.${code}`) : undefined);
}

interface FieldShell {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  children: (ids: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
  className?: string;
}

export function Field({ label, hint, error, optional, children, className }: FieldShell) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`field${error ? " field--invalid" : ""}${className ? ` ${className}` : ""}`}>
      <label htmlFor={id} className="field__label">
        {label}
        {optional && <span className="field__optional"> (isteğe bağlı)</span>}
      </label>
      {hint && (
        <p id={hintId} className="field__hint">
          {hint}
        </p>
      )}
      {children({ id, describedBy, invalid: Boolean(error) })}
      {error && (
        <p id={errId} className="field__error">
          {error}
        </p>
      )}
    </div>
  );
}

type TextProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id"> & { label: ReactNode; hint?: ReactNode; error?: string; optional?: boolean };
export function TextField({ label, hint, error, optional, className, ...rest }: TextProps) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} className={className}>
      {({ id, describedBy, invalid }) => <input id={id} className="input" aria-describedby={describedBy} aria-invalid={invalid || undefined} {...rest} />}
    </Field>
  );
}

type AreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> & { label: ReactNode; hint?: ReactNode; error?: string; optional?: boolean };
export function TextArea({ label, hint, error, optional, className, rows = 3, ...rest }: AreaProps) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} className={className}>
      {({ id, describedBy, invalid }) => <textarea id={id} rows={rows} className="input textarea" aria-describedby={describedBy} aria-invalid={invalid || undefined} {...rest} />}
    </Field>
  );
}

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> & {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  options: { value: string; label: string }[];
  placeholder?: string;
};
/** Yerel <select>: mobilde platformun kendi seçicisi açılır (en erişilebilir seçenek). */
export function SelectField({ label, hint, error, optional, options, placeholder, className, ...rest }: SelectProps) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} className={className}>
      {({ id, describedBy, invalid }) => (
        <select id={id} className="input select" aria-describedby={describedBy} aria-invalid={invalid || undefined} {...rest}>
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function Checkbox({ label, hint, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { label: ReactNode; hint?: ReactNode }) {
  const id = useId();
  return (
    <div className="check">
      <input id={id} type="checkbox" {...rest} />
      <label htmlFor={id}>
        {label}
        {hint && <span className="check__hint">{hint}</span>}
      </label>
    </div>
  );
}

/** Birbirini dışlayan kısa seçenekler için (yarım gün, tür vb.). Yerel radyo düğmeleri. */
export function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
  name,
}: {
  label: ReactNode;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; hint?: string }[];
  name: string;
}) {
  return (
    <fieldset className="segmented">
      <legend className="field__label">{label}</legend>
      <div className="segmented__options">
        {options.map((o) => (
          <label key={o.value} className={`segmented__option${value === o.value ? " is-selected" : ""}`}>
            <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
            <span>{o.label}</span>
            {o.hint && <span className="segmented__hint">{o.hint}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Form başındaki hata özeti (ekran okuyucuya `role="alert"` ile okunur). Yeni bir hata geldiğinde odak ilk hatalı
 * alana taşınır (docs/UX.md §5); hatalı alan yoksa özetin kendisine. Yazarken odak kaymaz; ayrıntılı açıklama her
 * alanın altındadır.
 */
export function ErrorSummary({ message, fields }: { message?: string | null; fields?: Record<string, string> }) {
  const ref = useRef<HTMLDivElement>(null);
  const count = Object.keys(fields ?? {}).length;
  useEffect(() => {
    if (!message || !ref.current) return;
    const scope = ref.current.closest("form") ?? ref.current.closest('[role="dialog"]') ?? ref.current.parentElement;
    const first = scope?.querySelector<HTMLElement>('input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"], [role="combobox"][aria-invalid="true"]');
    (first ?? ref.current).focus();
  }, [message, count]);
  if (!message) return null;
  return (
    <div className="error-summary" role="alert" tabIndex={-1} ref={ref}>
      <p className="error-summary__title">{message}</p>
      {count > 0 && <p>{count === 1 ? "Bir alanı düzeltin" : `${count} alanı düzeltin`}; ne gerektiği alanın altında yazıyor.</p>}
    </div>
  );
}
